import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Camera, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { apiGet } from "@/lib/api";
import { openQuoteWizard } from "@/pages/LandingPage";
import QuoteWizard from "@/components/landing/QuoteWizard";

export default function PortfolioPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    apiGet("/portfolio", { limit: 60 })
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header onQuoteClick={() => setWizardOpen(true)} />

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-16 sm:pb-24">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-sky-300 transition-colors mb-6 text-sm"
          data-testid="portfolio-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 sm:mb-12"
        >
          <span className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-3 block">Our Work</span>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-7xl text-white font-light">
            The <span className="gradient-text font-medium">Portfolio</span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base mt-4 flex items-center gap-2 max-w-2xl">
            <Camera className="w-4 h-4 shrink-0" />
            Real work from real customers across Maricopa &amp; surrounding cities.
          </p>
        </motion.div>

        {items === null ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
            <p className="text-gray-400">Loading portfolio…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="portfolio-empty">
            <p className="text-gray-400">New photos of our work are coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {items.map((it, i) => (
              <motion.button
                key={`${it.url}-${i}`}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: (i % 8) * 0.04 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => setActive(it)}
                data-testid={`portfolio-item-${i}`}
                className="relative aspect-square rounded-lg overflow-hidden bg-white/5 group"
              >
                {it.kind === "video" ? (
                  <video src={it.url} className="w-full h-full object-cover" muted loop />
                ) : (
                  <img src={it.url} alt="" className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                  <div className="flex items-center gap-1">
                    {[...Array(it.rating || 5)].map((_, j) => (
                      <Star key={`s-${j}`} className="w-2.5 h-2.5 fill-sky-400 text-sky-400" />
                    ))}
                  </div>
                  <p className="text-white text-xs mt-1 truncate">
                    {it.author_name}
                    {it.source === "google" && <span className="text-sky-300 ml-1">· Google</span>}
                  </p>
                </div>
              </motion.button>
            ))}
          </div>
        )}

        <div className="mt-12 sm:mt-16 text-center rounded-2xl border border-sky-500/20 bg-sky-500/5 p-8">
          <h3 className="font-['Outfit'] text-2xl sm:text-3xl text-white mb-2">Want to be next?</h3>
          <p className="text-gray-400 mb-5 text-sm">Get an instant AI quote on your vehicle in under 60 seconds.</p>
          <button
            onClick={() => { setWizardOpen(true); openQuoteWizard(); }}
            data-testid="portfolio-cta"
            className="px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-400 text-white font-medium"
          >
            Get AI Quote
          </button>
        </div>
      </main>

      <Footer />

      {active && (
        <div
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          onClick={() => setActive(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-4xl max-h-[90vh]"
          >
            {active.kind === "video" ? (
              <video src={active.url} controls autoPlay className="max-w-full max-h-[80vh] rounded-xl" />
            ) : (
              <img src={active.url} alt="" className="max-w-full max-h-[80vh] rounded-xl" />
            )}
            <div className="mt-4 text-center text-gray-300 text-sm">
              <p>{active.author_name}</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                {[...Array(active.rating || 5)].map((_, j) => (
                  <Star key={`a-${j}`} className="w-3 h-3 fill-sky-400 text-sky-400" />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <QuoteWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}
